import { useEffect, useState } from "react"
import { useAction, useAtom } from "@reatom/npm-react"
import { toast } from "react-toastify"

import { isValidAnswer } from "../../common/utils/isValidAnswer"

import style from "./style.module.scss"

import { QuestionnairePage } from "modules/NewClientSurvey/components/QuestionnairePage"
import {
    checkIsInput,
    filterObject,
    sendAnalyticNextQuestion,
    SurveyAnswer,
    SurveyQuestion,
    SurveyQuestions,
    AnswerType,
    yesNoChoice,
    Choices,
} from "modules/NewClientSurvey/common"
import { Input, MultiSelect, Select } from "modules/NewClientSurvey/components/index"
import {
    answersAtom,
    changeInputAnswers,
    changeMultiSelectAnswers,
} from "modules/NewClientSurvey/common/models"
import { showNextButton } from "modules/NewClientSurvey/common/utils/showNextButton"
import { getInfo } from "modules/NewClientSurvey/common/utils/getInfo"
import { checkQuestionValue } from "modules/NewClientSurvey/components/Question/helpers"
import { EMPTY_ERROR } from "modules/NewClientSurvey/components/Question/constants"
import { ctx } from "modules/PsyCatalog/common/ctx"
import { Img } from "ui-kit/components-branded/Img"

interface Props {
    question: SurveyQuestion
    surveyQuestions: SurveyQuestions
    nextPage: (state: Record<string, SurveyAnswer>) => Promise<void>
}

export function Question({ question, surveyQuestions, nextPage }: Props) {
    const { id, type, title, required, sequence_number, step, choices, ref } = question
    const isInput = checkIsInput(type)
    const [error, changeError] = useState<string | undefined>(
        required && !isInput ? EMPTY_ERROR : undefined
    )
    const [answers] = useAtom(answersAtom)
    const changeInput = useAction(changeInputAnswers)
    const changeMultiSelect = useAction(changeMultiSelectAnswers)

    const answerValue = answers[id]?.value

    const inputValue = answers[id]

    useEffect(() => {
        if (!required) return
        changeError(answerValue ? undefined : EMPTY_ERROR)
    }, [answerValue])

    useEffect(() => {
        if (inputValue && checkQuestionValue(question, inputValue, changeError)) {
            changeError(undefined)
        }
    }, [inputValue])

    function handleClickButton() {
        sendAnalyticNextQuestion(answers, id, surveyQuestions.title)
        nextPage(answers).catch((e) => console.log(e))
    }

    function handleClickSelect(questionId: string, choiceId: string, sequence_number: number) {
        const newState = answersAtom(ctx, (state) => {
            if (state[questionId]?.value === choiceId) return state
            else {
                const filteredState = filterObject(state, "sequence_number", sequence_number)

                return {
                    ...filteredState,
                    [questionId]: { value: choiceId, sequence_number, type, ref },
                } as Record<string, SurveyAnswer>
            }
        })

        sendAnalyticNextQuestion(newState, id, surveyQuestions.title)
        nextPage(newState).catch((e) => console.log(e))
    }

    function handleChangeInput(id: string, value: string | number, sequence_number: number) {
        changeInput(id, value, sequence_number, type, ref)
    }

    function handleChangeMultiSelect(id: string, choice: Choices, sequence_number: number) {
        changeMultiSelect(id, choice, sequence_number, type, ref)
    }

    const info = getInfo(question)

    useEffect(() => {
        if (!isValidAnswer(question, answerValue)) {
            toast.error(
                <div className={style.toastInfo}>
                    <Img src="attention.png" />
                    {info}
                </div>
            )
        }
    }, [answerValue])

    return (
        <QuestionnairePage
            title={title}
            currentStep={step}
            maxStep={Number(surveyQuestions.maxStep || 1)}
            showButton={showNextButton(type, required)}
            disabledButton={!!error}
            info={info}
            isValidAnswer={isValidAnswer(question, answerValue)}
            onClickButton={handleClickButton}
        >
            {isInput && (
                <Input
                    id={id}
                    value={(inputValue?.value as string) || ""}
                    type={type === AnswerType.number ? "number" : "text"}
                    error={inputValue ? error : ""}
                    onChange={(id, value) => handleChangeInput(id, value, sequence_number)}
                />
            )}
            {(type === AnswerType.choice || type === AnswerType.yes_no) && (
                <Select
                    questionId={id}
                    choices={type === AnswerType.yes_no ? yesNoChoice : choices}
                    onClickItem={(id, value) => handleClickSelect(id, value, sequence_number)}
                />
            )}
            {type === AnswerType.choices && (
                <MultiSelect
                    questionId={id}
                    choices={choices}
                    onClickItem={(id, choice) =>
                        handleChangeMultiSelect(id, choice, sequence_number)
                    }
                />
            )}
        </QuestionnairePage>
    )
}
